/**
 * The global pass-through preset — how the design system reaches PrimeVue.
 *
 * PrimeVue runs **unstyled** (`main.ts`): none of its design CSS ships, behaviour and a11y stay.
 * This object attaches our class hooks to its structural DOM, and `tokens.css`'s `@layer app`
 * styles those hooks. One direction only: PrimeVue never styles us, we never reach into its
 * internals beyond the documented pass-through sections.
 *
 * Keys are PrimeVue's lowercase component names; `pc*` entries address nested child components
 * (Password renders an InputText inside itself). A hook that names a section PrimeVue does not
 * have simply never lands — additive and safe — so components not listed here (DataTable,
 * DatePicker) get their hooks in the workspace-surface pass, written against the rendered DOM
 * rather than from memory.
 *
 * `Message` carries its severity onto the root as `data-severity`, which is what
 * `.c-msg[data-severity="error"]` keys on — the pt function form receives the component props.
 */
import type { PrimeVuePTOptions } from "primevue/config";

export const preset: PrimeVuePTOptions = {
  button: { root: { class: "c-btn" } },
  inputtext: { root: { class: "c-input" } },
  password: {
    root: { class: "c-pass" },
    pcInputText: { root: { class: "c-input" } },
  },
  message: {
    root: ({ props }) => ({
      class: "c-msg",
      "data-severity": props.severity ?? "info",
    }),
  },
  menu: {
    root: { class: "c-menu" },
  },
  skeleton: { root: { class: "c-skel" } },
  datepicker: {
    root: { class: "c-dp" },
    pcInputText: { root: { class: "c-input" } },
    panel: { class: "c-dp-panel" },
  },
  datatable: {
    root: { class: "c-table" },
  },
  select: {
    root: { class: "c-select" },
    label: { class: "c-select-label" },
    dropdown: { class: "c-select-dropdown" },
    overlay: { class: "c-select-overlay" },
    listContainer: { class: "c-select-list-container" },
    list: { class: "c-select-list" },
    option: { class: "c-select-option" },
  },
  inputnumber: {
    root: { class: "c-inputnumber" },
    pcInputText: { root: { class: "c-input" } },
  },
  textarea: { root: { class: "c-input c-textarea" } },
};
